import { hasOwnProperty, isPlainObject } from '@spazious/dollhouse-utils';

import { EvalScope, ScopeImpl } from '../scope/index.js';

import { Schema, SchemaEvaluatedField, SchemaField, SchemaMergeArray } from './types.js';

export const isSchemaField = (obj: any): obj is SchemaField => {
  return isPlainObject(obj) && hasOwnProperty(obj, 'type');
};

export const createScopeFromSchemaField = (
  field: SchemaField,
  name: string,
  parent: any,
  opts: any
) => {
  switch (field.type) {
    case 'number':
    case 'boolean':
      return new EvalScope(field, name, parent, opts);

    default: {
      return new ScopeImpl(field, name, parent);
    }
  }
};

export const clampFieldValue = (field: SchemaEvaluatedField, value: any) => {
  switch (field.type) {
    case 'number':
      return typeof value === 'number'
        ? Math.max(field.min ?? -Infinity, Math.min(field.max ?? Infinity, value))
        : value;
    case 'vec2':
      const [x, y] = value ?? [];
      return typeof x === 'number' && typeof y === 'number'
        ? [
            Math.max(field.min?.[0] ?? -Infinity, Math.min(field.max?.[0] ?? Infinity, x)),
            Math.max(field.min?.[1] ?? -Infinity, Math.min(field.max?.[1] ?? Infinity, y)),
          ]
        : value;

    case 'boolean':
      return !!value;

    default:
      return value;
  }
};

export const mergeSchemas = <TSchemas extends Schema[]>(
  ...schemas: TSchemas
): SchemaMergeArray<TSchemas> =>
  schemas.slice(1).reduce((mergedSchema, schema) => {
    const { fields, rules, version, name, ...restSchema } = schema;

    if (mergedSchema.version !== version) {
      throw new Error(`Schema version mismatch: ${mergedSchema.version} !== ${version}`);
    }

    return {
      ...mergedSchema,
      name,
      ...restSchema,
      fields: Object.entries(fields).reduce((mergedFields, [key, field]) => {
        return {
          ...mergedFields,
          [key]: {
            ...mergedFields[key],
            ...field,
          },
        };
      }, mergedSchema.fields),
      rules: [...(mergedSchema.rules ?? []), ...(rules ?? [])],
    };
  }, schemas[0] as any) as any;

export const pickSchemaFields = <TSchema extends Schema, TKeys extends keyof TSchema['fields']>(
  schema: TSchema,
  keys: TKeys[]
): Pick<TSchema['fields'], TKeys> => {
  return keys.reduce(
    (acc, key) =>
      hasOwnProperty(schema.fields, key as string)
        ? { ...acc, [key]: schema.fields[key as string] }
        : acc,
    {} as any
  );
};
